import { EventMeta } from '../../../interfaces/meta.interface';

type RecipientPermissionDescFields = {
    recipient: string;
    accessRights: string;
};

const RecipientPermission: EventMeta<RecipientPermissionDescFields> = {
    title: 'Recipient Permission Added',
    default: {
        description: 'Recipient Permission Added',
        longDescription: 'Permission was added to a recipient.',
        severity: 'info',
    },
    descriptionExtractor: (log: any) => ({
        recipient: log.recipient,
        accessRights: log.accessRights,
    }),
    classifications: [
        {
            severity: 'critical',
            description: (fields) => `A suspicious IP added permission to a recipient`,
            longDescription: (fields) =>
                `Permission was added to a recipient from a suspicious IP address that is not in your allowed list. This activity may indicate an attempt to gain unauthorized access to emails or other resources.`,
            predicates: {
                isAllowedASN: false,
                isAllowedCountry: false,
                isAllowedRegion: false,
                isAllowedIP: false,
                isFraudulentIP: true,
                isMicrosoftActivity: false,
            },
        },
        {
            severity: 'danger',
            description: (fields) => `An unapproved IP added permission to a recipient`,
            longDescription: (fields) =>
                `Permission was added to a recipient from an IP address that is not in your allowed list. This activity may indicate an attempt to gain unauthorized access to emails or other resources.`,
            predicates: {
                isAllowedASN: false,
                isAllowedCountry: false,
                isAllowedRegion: false,
                isAllowedIP: false,
                isMicrosoftActivity: false,
            },
        },
        {
            severity: 'warning',
            description: (fields) => `A suspicious IP added a recipient permission`,
            longDescription: (fields) =>
                `Permission was added to a recipient. The action originated from a suspicious IP. While recipient permissions are often modified during routine configuration or delegation, the involvement of a suspicious IP makes this activity noteworthy. This event is informational — it does not imply immediate risk, but tracking similar changes from suspicious IPs can provide useful context for monitoring.`,
            predicates: {
                isFraudulentIP: true,
                isMicrosoftActivity: false,
            },
        },
        {
            severity: 'info',
            description: (fields) =>
                `A Microsoft system operation (synchronization server IP) added a recipient permission`,
            longDescription: (fields) =>
                `Permission was added to a recipient. This was identified as a Microsoft system operation event, with the IP address belonging to the Microsoft server responsible for synchronization. This event is informational only and reflects normal background activity. No action is required unless unexpected changes to recipient permissions are detected.`,
            predicates: {
                isMicrosoftActivity: true,
            },
        },
        {
            severity: 'info',
            description: (fields) => `A recipient permission was added`,
            longDescription: (fields) =>
                `Permission was added to a recipient. This is an informational record of a configuration change. No unusual conditions were noted in this event. It does not require immediate action unless the permission change was unexpected.`,
        },
    ],
};

export default RecipientPermission;
